import { supabase } from '../../client';
import { logError } from '@/lib/sentry';
import {
  BulkUpsertItemPayload,
  BulkUpsertMode,
  BulkUpsertResult,
  CreateInventoryItemInput,
  InventoryItem,
  InventoryLedgerEntry,
  InventoryOverview,
  InventoryQuickAdd,
  InventoryTodayKpis,
  LedgerReason,
  RecordMovementInput,
  UpdateInventoryItemInput,
} from '../types/inventory';
import type { InventoryFieldMapping } from '@/utils/inventoryExcel';

// 활성(미삭제) 상품 전체. GRANT는 SELECT + UPDATE(name, price, low_stock_threshold, deleted_at)만
// — INSERT/DELETE 없음, stock은 원장 트리거만 갱신(클라 쓰기 불가). RLS가 user_id로 스코프.
// PR3부터 useInventory는 이 함수 대신 getOverview()를 쓴다(items가 상위집합) — 다른 소비처가
// 생길 수 있어 함수 자체는 남겨둔다(무회귀: 기존 공개 함수 삭제는 이 태스크 범위 밖).
export async function fetchActiveItems(): Promise<InventoryItem[]> {
  const { data, error } = await supabase
    .from('inventory_item')
    .select('*')
    .is('deleted_at', null)
    .order('created_at', { ascending: false });
  if (error) { logError('fetchActiveItems', error); throw error; }
  return (data ?? []) as InventoryItem[];
}

// PR3 파생 예약: 활성 상품 마스터(+ reserved) + 윈도우 내 미매칭 주문 이름 집계를 한 왕복으로.
// RPC는 STABLE SECURITY DEFINER, caller auth.uid()로 fail-closed 스코프(비로그인 시 빈 구조).
// windowDays 생략 시 RPC 디폴트(90) 그대로 — 클램프(30..90)도 RPC 쪽 책임이라 여기서 재검증하지
// 않는다. RETURNS jsonb(스칼라, SETOF 아님)라 data가 곧 InventoryOverview 형태 그 자체다.
export async function getOverview(windowDays = 90): Promise<InventoryOverview> {
  const { data, error } = await supabase.rpc('get_inventory_overview_rpc', {
    p_window_days: windowDays,
  });
  if (error) { logError('getOverview', error); throw error; }
  return data as InventoryOverview;
}

// PR6: /order '내 상품' 빠른 추가 전용 경량판(get_inventory_quick_add_rpc). overview와 동일한
// STABLE SECURITY DEFINER + auth.uid() fail-closed 스코프(비로그인 시 {items: []}), 윈도우는
// RPC 고정(90d — overview 디폴트와 동일 시맨틱)이라 인자가 없다. RETURNS jsonb(스칼라)라
// data가 곧 InventoryQuickAdd 형태 그 자체(getOverview와 동일 캐스트 관례).
export async function getQuickAdd(): Promise<InventoryQuickAdd> {
  const { data, error } = await supabase.rpc('get_inventory_quick_add_rpc');
  if (error) { logError('getQuickAdd', error); throw error; }
  return data as InventoryQuickAdd;
}

// PR7-2: /inventory KPI 위젯의 오늘 입고/출고 집계(get_inventory_today_kpis_rpc). quick-add와
// 동일한 STABLE SECURITY DEFINER + auth.uid() fail-closed 스코프(비로그인 시 0/0)의 무인자 조회.
// RETURNS jsonb(스칼라, SETOF 아님)라 data가 곧 InventoryTodayKpis 형태 그 자체(getQuickAdd와
// 동일 캐스트 관례). 집계 시맨틱(입고='입고'만, 출고='출고'+'주문차감'−'주문복원' 순액)은
// RPC 쪽 단일 진실 — 클라에서 재집계하지 않는다.
export async function getTodayKpis(): Promise<InventoryTodayKpis> {
  const { data, error } = await supabase.rpc('get_inventory_today_kpis_rpc');
  if (error) { logError('getTodayKpis', error); throw error; }
  return data as InventoryTodayKpis;
}

// PR7-3(출시 팝업): "재고관리를 써 본 적 있는가" 존재 판정 — 소프트 삭제 포함(과거 사용 시맨틱:
// 베타 시절 상품을 등록했다가 전부 지운 유저도 '이미 아는 유저'다). quick-add RPC(예약 집계
// 스캔)를 존재 체크로 쓰지 않기 위한 경량 대체(codexloop PR7-3 R1 수용).
// 프로브 2개로 나누는 이유(codexloop R2 수용): inventory_item 인덱스는 둘 다 deleted_at partial
// (활성=uq_inventory_item_user_norm / 삭제=idx_inventory_item_user_norm_deleted)이라 deleted_at
// 조건 없는 단일 쿼리는 어느 쪽도 못 타고 풀스캔이 된다 — 이 함수의 주 대상(재고 미사용
// 활성 셀러)이 매 세션 그 비용을 반복하게 되므로, 각 partial 인덱스를 타는 존재 프로브를
// 순차 실행한다(활성 먼저 — 대다수 사용 유저는 첫 프로브에서 끝). RLS 본인 행 스코프.
export async function hasAnyItemEver(): Promise<boolean> {
  const active = await supabase.from('inventory_item').select('id').is('deleted_at', null).limit(1);
  if (active.error) { logError('hasAnyItemEver.active', active.error); throw active.error; }
  if ((active.data ?? []).length > 0) return true;
  const deleted = await supabase.from('inventory_item').select('id').not('deleted_at', 'is', null).limit(1);
  if (deleted.error) { logError('hasAnyItemEver.deleted', deleted.error); throw deleted.error; }
  return (deleted.data ?? []).length > 0;
}

// 원장은 GRANT SELECT만(쓰기는 RPC 경유) — 불변 원장이라 id 커서(keyset) 페이지네이션.
// offset/limit(codexloop R2 이전)은 두 페이지 사이에 새 행이 삽입되면 뒤 페이지가 "shift"되는
// 근본 결함이 있다: PAGE_SIZE개 이상의 신규 행이 끼면 offset이 가리키는 지점이 옛 페이지와
// 겹쳐 무한 반복(dedupe가 매 응답을 전부 제거 → hasMore는 계속 true인데 진행이 없음), 그보다
// 적게 끼면 반대로 아직 안 보여준 최신 행이 옛 행들 아래에 뒤섞여 붙는다(순서 붕괴). id
// keyset(beforeId 미만만 조회)은 커서가 절대 위치가 아니라 "마지막으로 본 행"이라 위 두 결함이
// 구조적으로 불가능하다.
// 정렬은 id desc만 — created_at 정렬(과거 tie-break)은 제거. inventory_ledger.id는 BIGINT
// GENERATED ALWAYS AS IDENTITY이고 테이블은 append-only(불변 원장, UPDATE/DELETE 없음)라
// id 자체가 이미 삽입 순서와 1:1로 단조증가한다 — id desc가 곧 최신순이다. created_at은
// 클록/타임존 편차로 동시각 근처에서 id 순서와 미세하게 어긋날 수 있어 오히려 부정확한
// tie-break였고, 지금은 화면 표시용 값일 뿐 정렬 키가 아니다.
// PR7 서버측 필터(itemId/reason/createdFrom/createdBefore)는 전부 optional·additive —
// keyset과 직교한다(id 단조증가는 필터가 만든 부분집합에서도 그대로 유지되므로 beforeId
// 커서에 결함이 생기지 않는다). createdFrom/createdBefore는 ISO timestamptz 문자열이며
// createdBefore는 exclusive 상한 — KST 달력 날짜 → 경계 변환은 service 책임.
export async function fetchLedgerPage(params: {
  beforeId?: number;
  limit: number;
  itemId?: number;
  reason?: LedgerReason;
  createdFrom?: string;
  createdBefore?: string;
}): Promise<InventoryLedgerEntry[]> {
  let query = supabase
    .from('inventory_ledger')
    .select('*')
    .order('id', { ascending: false })
    .limit(params.limit);
  if (params.beforeId !== undefined) {
    query = query.lt('id', params.beforeId);
  }
  if (params.itemId !== undefined) {
    query = query.eq('item_id', params.itemId);
  }
  if (params.reason !== undefined) {
    query = query.eq('reason', params.reason);
  }
  if (params.createdFrom !== undefined) {
    query = query.gte('created_at', params.createdFrom);
  }
  if (params.createdBefore !== undefined) {
    query = query.lt('created_at', params.createdBefore);
  }
  const { data, error } = await query;
  if (error) { logError('fetchLedgerPage', error); throw error; }
  return (data ?? []) as InventoryLedgerEntry[];
}

// 원장 상품명 머지용 보조 조회(FK 없는 구조라 embed 불가 — service가 2차 쿼리로 합성).
// 본인 행 SELECT RLS라 deleted_at 필터 없음 — 소프트 삭제된 상품도 이름이 그대로 조회된다
// (의도됨: 삭제 전 발생한 원장 행도 상품명을 계속 보여줘야 함).
export async function fetchItemNamesByIds(ids: number[]): Promise<Array<Pick<InventoryItem, 'id' | 'name'>>> {
  if (ids.length === 0) return [];
  const { data, error } = await supabase
    .from('inventory_item')
    .select('id, name')
    .in('id', ids);
  if (error) { logError('fetchItemNamesByIds', error); throw error; }
  return (data ?? []) as Array<Pick<InventoryItem, 'id' | 'name'>>;
}

// create_inventory_item_rpc: name은 원문 그대로 저장(RPC가 btrim 안 함 — service에서 trim 후 호출).
// 같은 norm의 소프트 삭제 행이 있으면 복원(+재고 조정), 활성 동명 행이면 duplicate 에러.
// SETOF 반환이라 배열로 오며, 정상 흐름이면 항상 1행 — 0행이면 계약 위반으로 취급해 throw.
export async function createItem(input: CreateInventoryItemInput): Promise<InventoryItem> {
  const { data, error } = await supabase.rpc('create_inventory_item_rpc', {
    p_name: input.name,
    p_initial_stock: input.initialStock,
    p_price: input.price,
    p_low_stock_threshold: input.lowStockThreshold,
  });
  if (error) { logError('createItem', error); throw error; }
  const rows = (data ?? []) as InventoryItem[];
  if (rows.length === 0) {
    const emptyResultError = new Error('create_inventory_item_rpc returned no row');
    logError('createItem.emptyResult', emptyResultError);
    throw emptyResultError;
  }
  return rows[0];
}

// record_inventory_movement_rpc: 입고/출고는 p_mode를 항상 null로 보낸다(RPC가 무시하지만 계약 명시).
// 조정은 mode 필수(set/add/subtract) — service가 검증 후 넘긴다. 반환값 = 반영 후 stock.
export async function recordMovement(input: RecordMovementInput): Promise<number> {
  const { data, error } = await supabase.rpc('record_inventory_movement_rpc', {
    p_item_id: input.itemId,
    p_type: input.type,
    p_qty: input.qty,
    p_mode: input.mode ?? null,
    p_memo: input.memo ?? null,
  });
  if (error) { logError('recordMovement', error); throw error; }
  return data as number;
}

// 소프트 삭제 = 클라 직접 update(GRANT 허용). 복원은 create_inventory_item_rpc 경유만(UI 배선 계약,
// 이 repo에 별도 restore 함수를 두지 않는다). 이미 삭제된 행(또는 stale id·RLS로 안 보이는 행)은
// .is('deleted_at', null) 조건에 안 걸려 0행 갱신되는데, 예전엔 이를 조용한 no-op으로 취급했다
// (codexloop R1 Fix C로 변경) — 0행이면 사용자에게 "삭제됐다"는 성공 토스트가 뜨지만 실제로는
// 아무 것도 안 바뀐 거짓 성공이라, deleteAccount(depositAccountRepository.ts) 선례와 동일하게
// .select('id')로 실제 갱신 행을 확인해 0행이면 명시적으로 throw한다. 메시지는
// isInventoryItemNotFoundError predicate가 잡는 어휘('inventory_item_not_found')로 맞춰
// useInventoryActions.softDeleteItem의 기존 매핑('상품을 찾을 수 없어요...' 토스트)을 그대로 탄다.
// 상품 정보 수정 = 클라 직접 update(GRANT: name/price/low_stock_threshold — stock은 컬럼 GRANT
// 자체가 없어 여기서 만질 수 없다). patch는 존재하는 필드만 전송(codexloop R1 #2 수용 —
// 전 필드 고정 전송은 다른 탭이 바꾼 필드를 stale 값으로 되덮는 lost-update를 만든다).
// norm_name은 GENERATED ALWAYS라 rename 시 DB가 자동 재계산 — 활성 동명 충돌은
// uq_inventory_item_user_norm이 23505로 거부. 번역은 여기 자기 스코프에서만 수행(codexloop R1
// #3 수용): 이 UPDATE가 건드릴 수 있는 유니크 제약은 uq_inventory_item_user_norm뿐이라
// 23505 = 동명 충돌 확정 — 'duplicate_inventory_item' 어휘로 바꿔 던져 기존 매핑('이미 등록된
// 상품이에요')을 태우고, 공유 predicate(isDuplicateInventoryItemError)는 원형 그대로 둔다.
// softDeleteItem과 동일하게 .is('deleted_at', null)+.select('id')로 0행 갱신(삭제됨·stale
// id·RLS 불가시 행)을 거짓 성공 대신 inventory_item_not_found로 명시 throw한다.
export async function updateItem(input: UpdateInventoryItemInput): Promise<void> {
  const patch: { name?: string; price?: number; low_stock_threshold?: number } = {};
  if (input.name !== undefined) patch.name = input.name;
  if (input.price !== undefined) patch.price = input.price;
  if (input.lowStockThreshold !== undefined) patch.low_stock_threshold = input.lowStockThreshold;
  const { data, error } = await supabase
    .from('inventory_item')
    .update(patch)
    .eq('id', input.id)
    .is('deleted_at', null)
    .select('id');
  if (error) {
    logError('updateItem', error);
    if ((error as { code?: string }).code === '23505') {
      throw new Error('duplicate_inventory_item');
    }
    throw error;
  }
  if (!data || data.length === 0) {
    const e = new Error('inventory_item_not_found');
    logError('updateItem', e);
    throw e;
  }
}

export async function softDeleteItem(id: number): Promise<void> {
  const { data, error } = await supabase
    .from('inventory_item')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)
    .is('deleted_at', null)
    .select('id');
  if (error) { logError('softDeleteItem', error); throw error; }
  if (!data || data.length === 0) {
    const e = new Error('inventory_item_not_found');
    logError('softDeleteItem', e);
    throw e;
  }
}

// 일괄 소프트 삭제(체크박스 다중 선택) — 단일 UPDATE 한 방(N왕복 아님). .is('deleted_at', null)
// 탓에 다른 탭에서 이미 삭제된 행은 매치되지 않는데, 이는 부분 성공으로 허용하고 실제 삭제된
// 개수를 반환한다(토스트가 정직한 개수를 말하도록). 전부 0행이면 softDeleteItem과 동일 어휘
// (inventory_item_not_found)로 throw해 기존 '상품을 찾을 수 없습니다...' 매핑을 그대로 탄다.
export async function softDeleteItems(ids: number[]): Promise<number> {
  const { data, error } = await supabase
    .from('inventory_item')
    .update({ deleted_at: new Date().toISOString() })
    .in('id', ids)
    .is('deleted_at', null)
    .select('id');
  if (error) { logError('softDeleteItems', error); throw error; }
  if (!data || data.length === 0) {
    const e = new Error('inventory_item_not_found');
    logError('softDeleteItems', e);
    throw e;
  }
  return data.length;
}

// PR5: bulk_upsert_inventory_items_rpc — items는 이미 T2 파서(extractInventoryRows)가 검증한
// {name, price, stock, lowStockThreshold} 행뿐(rowNo strip은 호출부 책임). RETURNS jsonb(스칼라)라
// data가 곧 BulkUpsertResult 그대로.
export async function bulkUpsertItems(
  items: BulkUpsertItemPayload[],
  mode: BulkUpsertMode,
): Promise<BulkUpsertResult> {
  const { data, error } = await supabase.rpc('bulk_upsert_inventory_items_rpc', {
    p_items: items,
    p_mode: mode,
  });
  if (error) { logError('bulkUpsertItems', error); throw error; }
  return data as BulkUpsertResult;
}

export interface HeaderMappingResponse {
  mapping: InventoryFieldMapping | null;
  confidence?: 'high' | 'low';
}

// PR5 ② AI 헤더 매핑(map-inventory-headers, non-critical accelerator) — 이 함수는 절대 throw하지
// 않는다. 네트워크 실패/타임아웃/예상 밖 응답 형태 등 어떤 사유로든 호출부(모달)가 ③ 수동 매핑으로
// 막힘 없이 폴백할 수 있도록 항상 {mapping:null}로 수렴시킨다(엣지함수 자체도 동일 계약으로
// 200+{mapping:null}을 반환하지만, invoke 왕복 실패 등 클라 측 예외까지 여기서 흡수).
export async function requestHeaderMapping(
  headers: string[],
  sampleRows: string[][],
): Promise<HeaderMappingResponse> {
  try {
    const { data, error } = await supabase.functions.invoke('map-inventory-headers', {
      body: { headers, sampleRows },
    });
    if (error) {
      logError('requestHeaderMapping', error);
      return { mapping: null };
    }
    if (!data || typeof data !== 'object' || !('mapping' in data)) {
      logError('requestHeaderMapping.malformed', new Error('map-inventory-headers 응답 형식이 올바르지 않음'));
      return { mapping: null };
    }
    return data as HeaderMappingResponse;
  } catch (err) {
    logError('requestHeaderMapping.catch', err);
    return { mapping: null };
  }
}
